import { OverallStarRating } from '../components/shared/recommendations/types';

/**
 * Determines if we should fetch profile rating data for a search result card.
 *
 * Strategy: Fetch for ALL profiles (no tier-based optimization)
 * - Ensures 100% consistency with profile page across all tiers
 * - Handles search API staleness in both directions:
 *   - Search has reviews but profile filters them out (NAW-123)
 *   - Search has no reviews but profile has them (stale search data)
 * - Fetching is parallel - minimal wall-clock impact
 */
export const shouldFetchProfileRating = (
  _profileTier?: string | null,
): boolean => true;

/**
 * Determines the final rating to display in search results.
 * Always uses profile data for 100% consistency across all tiers.
 *
 * Additional requirement: Must have reviews within last 12 months
 * (checks numberOfReviewsRecent > 0)
 */
export const getSearchResultRating = ({
  searchRecentReviewCount,
  profileRating,
  profileReviewCount,
  profileLoading,
}: {
  searchRecentReviewCount?: number | null;
  profileRating?: number | null;
  profileReviewCount?: number | null;
  profileLoading: boolean;
}): OverallStarRating | undefined => {
  if (searchRecentReviewCount === 0) {
    return undefined;
  }

  if (profileLoading) {
    return undefined;
  }

  const profileHasRating =
    profileRating !== null &&
    profileRating !== undefined &&
    profileReviewCount !== null &&
    profileReviewCount !== undefined &&
    profileReviewCount > 0;

  if (!profileHasRating) {
    return undefined;
  }

  const displayReviewCount =
    searchRecentReviewCount !== null && searchRecentReviewCount !== undefined
      ? Math.min(profileReviewCount, searchRecentReviewCount)
      : profileReviewCount;

  if (displayReviewCount === 0) {
    return undefined;
  }

  return {
    rating: profileRating,
    totalReviews: displayReviewCount,
  };
};
